import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { PEOOrganizationService } from '@/services/peo-organization.service';
import { Users, User, Shield } from 'lucide-react';

interface RoleAssignmentDialogProps {
  providerId: string;
  roleId?: string;
  selectedUserId?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export const RoleAssignmentDialog: React.FC<RoleAssignmentDialogProps> = ({
  providerId,
  roleId,
  selectedUserId: preSelectedUserId,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  children
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>(preSelectedUserId || '');
  const [selectedRoleId, setSelectedRoleId] = useState<string>(roleId || '');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Use external open state if provided, otherwise use internal state
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;

  // Fetch verified representatives for this provider
  const { data: representatives, isLoading: repsLoading } = useQuery({
    queryKey: ['enhanced-representatives', providerId],
    queryFn: () => PEOOrganizationService.getEnhancedRepresentatives(providerId),
    enabled: isOpen && !!providerId
  });

  // Fetch all organizational roles for this provider (only when role selection is needed)
  const { data: availableRoles, isLoading: rolesLoading } = useQuery({
    queryKey: ['organizational-roles', providerId],
    queryFn: () => PEOOrganizationService.getOrganizationalRoles(providerId),
    enabled: isOpen && !roleId && !!providerId
  });

  const isLoading = repsLoading || rolesLoading;
  const verifiedRepresentatives = representatives?.filter(rep => rep.verification_status === 'verified') || [];

  // Assign role mutation
  const assignRole = useMutation({
    mutationFn: async ({ userId, newRoleId }: { userId: string; newRoleId: string }) => {
      await PEOOrganizationService.updateRepresentativeRole(userId, newRoleId);
    },
    onSuccess: () => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['enhanced-representatives', providerId] });
      queryClient.invalidateQueries({ queryKey: ['organizational-roles', providerId] });
      queryClient.invalidateQueries({ queryKey: ['org-chart-nodes', providerId] });
      queryClient.invalidateQueries({ queryKey: ['organization-stats', providerId] });
      
      toast({
        title: "Role Assigned",
        description: "Role has been successfully assigned to the representative."
      });
      handleOpenChange(false);
      setSelectedUserId(preSelectedUserId || '');
      setSelectedRoleId(roleId || '');
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to assign role.",
        variant: "destructive"
      });
    }
  });

  const handleAssign = () => {
    if (selectedUserId && selectedRoleId) {
      assignRole.mutate({ userId: selectedUserId, newRoleId: selectedRoleId });
    }
  };

  // Reset form when dialog opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      setSelectedUserId(preSelectedUserId || '');
      setSelectedRoleId(roleId || '');
    }
  };

  // Get role name for display
  const getSelectedRoleName = () => {
    if (roleId) {
      return availableRoles?.find(role => role.id === roleId)?.role_name;
    }
    return selectedRoleId ? availableRoles?.find(role => role.id === selectedRoleId)?.role_name : null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Assign Role to Representative
          </DialogTitle>
          <DialogDescription>
            {roleId 
              ? `Assign the "${getSelectedRoleName()}" role to a verified representative.`
              : "Select a role and representative for assignment."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Role Selection - only show if no roleId provided */}
              {!roleId && (
                <div className="space-y-3">
                  <label className="text-sm font-medium">Select Role:</label>
                  <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a role..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRoles?.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          <div className="flex items-center gap-3 w-full">
                            <Shield className="h-4 w-4" />
                            <div className="flex-1 text-left">
                              <div className="font-medium">{role.role_name}</div>
                              {role.description && (
                                <div className="text-xs text-muted-foreground">
                                  {role.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Representative Selection */}
              {verifiedRepresentatives.length > 0 ? (
                <div className="space-y-3">
                  <label className="text-sm font-medium">Select Representative:</label>
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a representative..." />
                    </SelectTrigger>
                    <SelectContent>
                      {verifiedRepresentatives.map((rep) => (
                        <SelectItem key={rep.user_id} value={rep.user_id}>
                          <div className="flex items-center gap-3 w-full">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs">
                                {rep.profile?.first_name?.[0]}{rep.profile?.last_name?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 text-left">
                              <div className="font-medium">
                                {rep.profile?.first_name} {rep.profile?.last_name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {rep.profile?.email}
                              </div>
                              {rep.organizational_role && (
                                <Badge variant="outline" className="text-xs mt-1">
                                  Current: {rep.organizational_role.role_name}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="text-center py-8">
                  <User className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No verified representatives found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Representatives must be verified before roles can be assigned.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssign}
            disabled={!selectedUserId || !selectedRoleId || assignRole.isPending}
          >
            {assignRole.isPending ? 'Assigning...' : 'Assign Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};